<script setup lang="ts">
import { computed } from 'vue';

import { GrammarDivider } from '@nora-monorepo/grammar-ui';
import type { DrawLayerResponseDTO } from '@nora-monorepo/nora-api-client';

import DrawLayerMenuItem from '#nora-ui/map/components/overlay-menu/items/DrawLayerMenuItem.vue';
import MenuGroup from '#nora-ui/map/components/overlay-menu/primitives/MenuGroup.vue';
import { useDrawLayerReorder } from '#nora-ui/map/composables/useDrawLayerReorder';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface DrawLayersGroupProps {
  mapId: string;
  operationId: string;
}

const props = defineProps<DrawLayersGroupProps>();

const mapStore = useMapStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const operationDrawLayers = computed(() => {
  return map.state.draw.drawLayersForOperation(props.operationId);
});

/**
 * The operation's draw layers split into the user's private drafts and the
 * layers shared with the operation. Empty sections are not rendered.
 */
const drawLayerSections = computed(() => {
  return [
    {
      title: 'Private',
      drawLayers: operationDrawLayers.value.filter(drawLayer => drawLayer.draft),
    },
    {
      title: 'Shared',
      drawLayers: operationDrawLayers.value.filter(drawLayer => !drawLayer.draft),
    },
  ].filter(section => section.drawLayers.length > 0);
});

function areAllLayersVisible(drawLayers: DrawLayerResponseDTO[]): boolean {
  return drawLayers.every(drawLayer => map.state.draw.isDrawLayerVisible(drawLayer.id));
}

function toggleLayersVisibility(drawLayers: DrawLayerResponseDTO[]) {
  const newValue = !areAllLayersVisible(drawLayers);

  drawLayers.forEach((drawLayer) => {
    map.state.draw.setDrawLayerVisibility(drawLayer.id, newValue);
  });
}

/**
 * Checks if two draw layers belong to the same section based on their `draft` status.
 * This limits drag/drop reordering to within a single section.
 */
function isSameSection(firstDrawLayerId: string, secondDrawLayerId: string): boolean {
  const first = operationDrawLayers.value.find(drawLayer => drawLayer.id === firstDrawLayerId);
  const second = operationDrawLayers.value.find(drawLayer => drawLayer.id === secondDrawLayerId);

  return first !== undefined && second !== undefined && first.draft === second.draft;
}

const {
  draggedItemId,
  dragOverItemId,
  dragDirection,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
} = useDrawLayerReorder({ map, canReorder: isSameSection });
</script>

<template>
  <MenuGroup
    v-for="section of drawLayerSections"
    :key="section.title"
    :visible="areAllLayersVisible(section.drawLayers)"
    :toggle-visibility="() => toggleLayersVisibility(section.drawLayers)"
    :title="section.title"
    :badge-amount="section.drawLayers.length"
  >
    <div
      v-for="(drawLayer, index) of section.drawLayers"
      :key="drawLayer.id"
      :class="{
        'drag-over-above': dragOverItemId === drawLayer.id && dragDirection === 'above',
        'drag-over-below': dragOverItemId === drawLayer.id && dragDirection === 'below',
        'dragging': draggedItemId === drawLayer.id,
      }"
      @dragover="handleDragOver(drawLayer.id, $event)"
      @dragleave="handleDragLeave($event)"
      @drop="handleDrop(drawLayer.id, $event)"
    >
      <DrawLayerMenuItem
        :draw-layer="drawLayer"
        :map-id="mapId"
        :operation-id="operationId"
        @drag-start="handleDragStart(drawLayer.id, $event)"
        @drag-end="handleDragEnd()"
      />

      <GrammarDivider
        v-if="index !== section.drawLayers.length - 1"
        emphasis="low-emphasis"
        class="px-2"
      />
    </div>
  </MenuGroup>
</template>

<style scoped>
.dragging,
.drag-over-above,
.drag-over-below {
  position: relative;
}

.drag-over-above,
.drag-over-below {
  transition: all 0.2s ease;
}

.dragging {
  opacity: 0.4;
}

.drag-over-above::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: var(--tpg-color-primary-500, #0066cc);
  border-radius: 2px;
  box-shadow: 0 0 4px rgba(0, 102, 204, 0.5);
}

.drag-over-below::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: var(--tpg-color-primary-500, #0066cc);
  border-radius: 2px;
  box-shadow: 0 0 4px rgba(0, 102, 204, 0.5);
}
</style>
